import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'

interface AppDownloadNudgeDialogProps {
  isSticky: boolean
  open: boolean
  onClose: () => void
  onOpenApp: () => void
  onContinueWeb: () => void
}

/* ---------------- Dialog ---------------- */

const BottomSheetDialog = styled(Dialog)(() => ({
  '& .MuiDialog-paper': {
    margin: 0,
    width: '100%',
    maxWidth: '100%',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    position: 'fixed',
    bottom: 0,
    overflow: 'hidden',
  },
}))

/* ---------------- Header ---------------- */

const Header = styled(Box)(() => ({
  position: 'absolute',
  top: 8,
  right: 8,
  zIndex: 2,
}))

/* ---------------- Illustration Section ---------------- */

const IllustrationWrapper = styled(Box)(() => ({
  position: 'relative',
  width: '100%',
  height: 180,
  backgroundColor: '#F6D65D',
  backgroundImage: "url('/downloadcover.png')",
  backgroundSize: 'cover',
  backgroundPosition: 'start',
  overflow: 'visible', // 🔑 allow overlap
}))

const Illustration = styled('img')(() => ({
  position: 'absolute',
  bottom: -40, // 👈 half overlaps content
  left: '50%',
  transform: 'translateX(-50%)',
  width: 80,
  zIndex: 2,
}))

/* ---------------- Content ---------------- */

const Content = styled(DialogContent)(() => ({
  textAlign: 'center',
  padding: '64px 24px 24px', // 🔑 extra top padding
}))

const Title = styled(Typography)(() => ({
  fontSize: '1.4rem',
  fontWeight: 700,
  marginBottom: 8,
}))

const Description = styled(Typography)(() => ({
  fontSize: '0.95rem',
  color: '#555',
}))

/* ---------------- Actions ---------------- */

const Actions = styled(DialogActions)(() => ({
  flexDirection: 'column',
  padding: '0 24px 24px',
  gap: 12,
}))

const PrimaryButton = styled(Button)(() => ({
  width: '100%',
  padding: '14px',
  fontSize: '1rem',
  fontWeight: 600,
  borderRadius: 12,
  backgroundColor: 'primary',
}))

const SecondaryButton = styled(Button)(() => ({
  fontSize: '0.9rem',
  textTransform: 'none',
}))

/* ---------------- Component ---------------- */

const AppDownloadNudgeDialog: React.FC<AppDownloadNudgeDialogProps> = ({
  isSticky,
  open,
  onClose,
  onOpenApp,
  onContinueWeb,
}) => {
  return (
    <BottomSheetDialog
      open={open}
      onClose={() => {
        if (!isSticky) {
          onClose()
        }
      }}
    >
      <Header>
        {!isSticky && (
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </Header>

      {/* Background + Illustration */}
      <IllustrationWrapper>
        <Illustration src="/logo192.png" alt="App Preview" />
      </IllustrationWrapper>

      <Content>
        <Description>Get the edvi app for</Description>
        <Title>Smoother, faster, better experience and performance</Title>
        <Description>Never miss an update.</Description>
      </Content>

      <Actions>
        <PrimaryButton variant="contained" onClick={onOpenApp}>
          Download the app now
        </PrimaryButton>

        {!isSticky && (
          <SecondaryButton onClick={onContinueWeb}>
            Continue on web
          </SecondaryButton>
        )}
      </Actions>
    </BottomSheetDialog>
  )
}

export default AppDownloadNudgeDialog
